import { Component } from '@theme/component';
import { DESTINATION_STATE_CHANGE, getDestinationState, getStateInfo } from '@theme/destination-state';

const ACTIVE_CLASS = '_active';
const VISIBLE_CLASS = '_visible';
const FLIPPED_CLASS = '_flipped';
const PIN_SELECTOR = '[ref="pins[]"]';

const STATE_DESTINATION_CLASS = 'us-partner-map__state--destination';
const STATE_SHIPPING_CLASS = 'us-partner-map__state--shipping';
const STATE_UNAVAILABLE_CLASS = 'us-partner-map__state--unavailable';
const PIN_AVAILABLE_CLASS = 'us-partner-map__pin--available';
const PIN_UNAVAILABLE_CLASS = 'us-partner-map__pin--unavailable';
const STATUS_AVAILABLE_CLASS = 'us-partner-map__tooltip-status--available';
const STATUS_NONE_CLASS = 'us-partner-map__tooltip-status--none';
const STATUS_SELECTOR = '[data-map-status]';
const SCOPE_SELECTOR = '[data-map-scope]';
const COUNT_SELECTOR = '[data-map-count]';
const STATE_NAME_SELECTOR = '[data-map-state-name]';
const DAYS_PLACEHOLDER = '{days}';

/**
 * Partner map pins + tooltips (FindARancherDemo PartnerMap.tsx pin layer and
 * Tooltip), wrapping snippets/us-partner-map.liquid.
 *
 * Every link pin is followed by its server-rendered tooltip (referenced by
 * the pin's `aria-describedby`). Hovering (mouse / pen) or focusing a pin
 * shows its tooltip: `_active` on the pin, `_visible` on the tooltip. Leaving
 * the pin hides it — unless another pin is still focused (keyboard) or
 * hovered, which then keeps / takes the tooltip, like the demo's
 * `onMouseLeave` that clears only when nothing is focused. Escape hides the
 * tooltip and keeps focus. Touch taps are ignored for the tooltip — the pin
 * is a plain link and navigates.
 *
 * Keyboard: the pins are ONE tab stop (roving `tabindex`); Arrow keys move
 * to the next / previous pin (wrapping), Home / End to the first / last.
 *
 * Positioning — why custom properties: 05-javascript.md § "State and
 * storage" forbids inline styles from JS except `style.setProperty('--x', …)`
 * for continuous values, and CSS anchor positioning is not usable across the
 * support matrix yet (04-css-bem.md § Modern CSS). So the script only
 * computes the geometry and writes it as `--us-partner-map-tooltip-x` / `-y`
 * (px from the map box's top-left corner) and `--us-partner-map-tooltip-stem-x`
 * (px from the tooltip's left edge) on the tooltip; the stylesheet consumes
 * them with `left` / `top`. The tooltip sits above the pin, is clamped
 * `--us-partner-map-tooltip-inset` from the map box's sides, and flips below
 * the pin (`_flipped`) when there is less than `--us-partner-map-tooltip-
 * headroom` above it. Those lengths and `--us-partner-map-tooltip-offset`
 * (stem + gap) are READ from the stylesheet, so CSS is the single source of
 * the metrics. The map box size is tracked with a ResizeObserver, which also
 * repositions a visible tooltip.
 *
 * Destination (when the root carries `data-follow-destination`): the server
 * renders the highlight for its `destination` param (`data-destination`);
 * on connect, after a Section Rendering re-render and on every
 * `destination-state:change` the component re-renders it for the store's
 * state (`getDestinationState()` from assets/destination-state.js — already
 * resolved when this module runs, since it is a static import, so the first
 * sync happens in `connectedCallback` with no extra frame). For the new
 * state code it (a) moves `--destination` to the path with that
 * `data-state` and gives every other path its base class (`--shipping`, or
 * `--unavailable` when `data-ships="false"`), (b) sets each pin / dot to
 * `--available` / `--unavailable` from its `data-lanes`, (c) rewrites each
 * tooltip's `[data-map-status]` ("Ships in {days}" — the ranch's one
 * `data-shipping-time` as "3 days" / "1 day", the same for every state it
 * serves; "Ships here" when a served ranch has no shipping time; or "No
 * service"; copy from the root's `data-status-*`), and (d) inside the
 * nearest `[data-map-scope]` ancestor, rewrites `[data-map-count]` (live
 * ranches whose lanes in `data-live-lanes` contain the state) and
 * `[data-map-state-name]` (the registry name). Classes, attributes and text
 * only. A code the registry does not know (or no store state) changes
 * nothing; a known state with no outline (AK, HI) clears the highlight.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement[]} [pins] - Link pins, each with `aria-describedby` → its tooltip, `data-x` / `data-y` percentages, `data-lanes` and `data-shipping-time`.
 * @property {HTMLElement[]} [dots] - Non-link pins (no ranch page), each with `data-lanes`.
 * @property {SVGPathElement[]} [states] - State outlines, each with `data-state`.
 *
 * @extends {Component<Refs>}
 */
class UsPartnerMapComponent extends Component {
  /** @type {Map<HTMLElement, HTMLElement>} */
  #tooltips = new Map();

  /** @type {Map<HTMLElement, HTMLElement>} Link pin → its tooltip's status span. */
  #statuses = new Map();

  /** State code the markup currently shows ('' = none). */
  #destination = '';

  /** @type {HTMLElement | null} */
  #activePin = null;

  /** @type {HTMLElement | null} */
  #hoveredPin = null;

  /** @type {HTMLElement | null} */
  #focusedPin = null;

  #size = { width: 0, height: 0 };

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#resolveTooltips();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener('pointerover', this.#showOnPinHover, { signal });
    this.addEventListener('pointerout', this.#hideOnPinLeave, { signal });
    this.addEventListener('focusin', this.#showOnPinFocus, { signal });
    this.addEventListener('focusout', this.#hideOnPinBlur, { signal });
    this.addEventListener('keydown', this.#moveFocusOnArrowKeys, { signal });
    this.addEventListener('keydown', this.#hideOnEscape, { signal });
    window.addEventListener('pageshow', this.#hideOnRestore, { signal });

    this.#resizeObserver = new ResizeObserver(this.#repositionOnResize);
    this.#resizeObserver.observe(this);

    this.#destination = this.dataset.destination ?? '';
    if (this.#followsDestination) {
      this.#validateDestinationContract();
      document.addEventListener(DESTINATION_STATE_CHANGE, this.#followDestinationOnChange, { signal });
      this.#renderDestination(getDestinationState());
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#hide();
    this.#resolveTooltips();

    // The re-render brought back the server highlight: re-apply the store's.
    this.#destination = this.dataset.destination ?? '';
    if (this.#followsDestination) {
      this.#validateDestinationContract();
      this.#renderDestination(getDestinationState());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#hide();
    this.#hoveredPin = null;
    this.#focusedPin = null;
  }

  /**
   * Pairs every pin with its tooltip (the markup contract — a pin without
   * its tooltip is a rendering bug) and sets up the roving tab stop.
   */
  #resolveTooltips() {
    const pins = this.refs.pins ?? [];
    this.#tooltips.clear();
    this.#statuses.clear();

    for (const pin of pins) {
      const tooltipId = pin.getAttribute('aria-describedby');
      const tooltip = tooltipId ? document.getElementById(tooltipId) : null;
      if (!tooltip || !this.contains(tooltip)) {
        throw new Error(`us-partner-map-component: no tooltip for pin "${pin.getAttribute('aria-label') ?? ''}"`);
      }
      const status = tooltip.querySelector(STATUS_SELECTOR);
      if (!(status instanceof HTMLElement)) {
        throw new Error(`us-partner-map-component: no ${STATUS_SELECTOR} in tooltip "${tooltipId}"`);
      }
      this.#tooltips.set(pin, tooltip);
      this.#statuses.set(pin, status);
    }

    const tabStop = this.#focusedPin && this.#tooltips.has(this.#focusedPin) ? this.#focusedPin : pins[0];
    if (tabStop) this.#setTabStop(tabStop);
  }

  /**
   * @param {HTMLElement} tabStop - The one pin left in the tab order.
   */
  #setTabStop(tabStop) {
    for (const pin of this.#tooltips.keys()) pin.tabIndex = pin === tabStop ? 0 : -1;
  }

  /** True when the map follows the site-wide destination state. */
  get #followsDestination() {
    return this.hasAttribute('data-follow-destination');
  }

  /**
   * The root attributes the destination render reads — rendered by
   * snippets/us-partner-map.liquid, so a missing one is a rendering bug.
   */
  #validateDestinationContract() {
    const contract = ['data-live-lanes', 'data-status-available', 'data-status-available-fallback', 'data-status-unavailable'];
    for (const attribute of contract) {
      if (!this.hasAttribute(attribute)) {
        throw new Error(`us-partner-map-component: missing ${attribute} on a map with data-follow-destination`);
      }
    }
  }

  /**
   * Re-renders the highlight for the state the store just changed to.
   */
  #followDestinationOnChange = () => {
    this.#renderDestination(getDestinationState());
  };

  /**
   * Re-renders every destination-dependent part of the map (see the class
   * doc). No-op for an unknown code or the code already shown.
   *
   * @param {string} code - Two-letter state code from the store.
   */
  #renderDestination(code) {
    const info = code ? getStateInfo(code) : null;
    if (!info || info.code === this.#destination) return;

    const destination = info.code;
    this.#destination = destination;
    this.dataset.destination = destination;

    for (const path of this.refs.states ?? []) {
      const isDestination = path.dataset.state === destination;
      const ships = path.dataset.ships !== 'false';
      path.classList.toggle(STATE_DESTINATION_CLASS, isDestination);
      path.classList.toggle(STATE_SHIPPING_CLASS, !isDestination && ships);
      path.classList.toggle(STATE_UNAVAILABLE_CLASS, !isDestination && !ships);
    }

    for (const pin of [...(this.refs.pins ?? []), ...(this.refs.dots ?? [])]) {
      const available = toCodes(pin.dataset.lanes).includes(destination);
      pin.classList.toggle(PIN_AVAILABLE_CLASS, available);
      pin.classList.toggle(PIN_UNAVAILABLE_CLASS, !available);
    }

    const availableTemplate = this.dataset.statusAvailable ?? '';
    const availableFallback = this.dataset.statusAvailableFallback ?? '';
    const unavailableText = this.dataset.statusUnavailable ?? '';
    for (const [pin, status] of this.#statuses) {
      const available = toCodes(pin.dataset.lanes).includes(destination);
      let text = unavailableText;
      if (available) {
        const days = shippingDaysText(pin.dataset.shippingTime);
        text = days ? availableTemplate.split(DAYS_PLACEHOLDER).join(days) : availableFallback;
      }
      if (status.textContent !== text) status.textContent = text;
      status.classList.toggle(STATUS_AVAILABLE_CLASS, available);
      status.classList.toggle(STATUS_NONE_CLASS, !available);
      // Server-rendered `hidden` while the map had no destination.
      status.hidden = false;
    }

    // A visible tooltip may have changed width with its status text.
    const activeTooltip = this.#activePin ? this.#tooltips.get(this.#activePin) : undefined;
    if (this.#activePin && activeTooltip) this.#position(this.#activePin, activeTooltip);

    const scope = this.closest(SCOPE_SELECTOR);
    if (!scope) return;

    const count = String(countShipping(this.dataset.liveLanes, destination));
    for (const node of scope.querySelectorAll(COUNT_SELECTOR)) {
      if (node.textContent !== count) node.textContent = count;
    }
    for (const node of scope.querySelectorAll(STATE_NAME_SELECTOR)) {
      if (node.textContent !== info.name) node.textContent = info.name;
    }
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null} The pin the target belongs to, if any.
   */
  #pinFrom(target) {
    if (!(target instanceof Element)) return null;
    const pin = target.closest(PIN_SELECTOR);
    return pin instanceof HTMLElement && this.#tooltips.has(pin) ? pin : null;
  }

  /**
   * @param {PointerEvent} event
   */
  #showOnPinHover = (event) => {
    if (event.pointerType === 'touch') return;
    const pin = this.#pinFrom(event.target);
    if (!pin) return;

    this.#hoveredPin = pin;
    this.#show(pin);
  };

  /**
   * @param {PointerEvent} event
   */
  #hideOnPinLeave = (event) => {
    const pin = this.#pinFrom(event.target);
    if (!pin || pin !== this.#hoveredPin) return;

    this.#hoveredPin = null;
    if (this.#focusedPin) this.#show(this.#focusedPin);
    else this.#hide();
  };

  /**
   * @param {FocusEvent} event
   */
  #showOnPinFocus = (event) => {
    const pin = this.#pinFrom(event.target);
    if (!pin) return;

    this.#focusedPin = pin;
    this.#setTabStop(pin);
    this.#show(pin);
  };

  /**
   * @param {FocusEvent} event
   */
  #hideOnPinBlur = (event) => {
    if (!this.#pinFrom(event.target)) return;
    // Focus moving to another pin: its focusin takes the tooltip over.
    if (this.#pinFrom(event.relatedTarget)) return;

    this.#focusedPin = null;
    if (this.#hoveredPin) this.#show(this.#hoveredPin);
    else this.#hide();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #moveFocusOnArrowKeys = (event) => {
    const pin = this.#pinFrom(event.target);
    if (!pin) return;

    const pins = [...this.#tooltips.keys()];
    const index = pins.indexOf(pin);
    let next;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = pins[(index + 1) % pins.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = pins[(index - 1 + pins.length) % pins.length];
        break;
      case 'Home':
        next = pins[0];
        break;
      case 'End':
        next = pins[pins.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    next?.focus();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #hideOnEscape = (event) => {
    if (event.key !== 'Escape' || !this.#activePin) return;
    this.#hide();
  };

  /**
   * A page restored from the back/forward cache must not show the tooltip
   * of the pin that was clicked to leave it.
   *
   * @param {PageTransitionEvent} event
   */
  #hideOnRestore = (event) => {
    if (!event.persisted) return;
    this.#hoveredPin = null;
    this.#hide();
  };

  /**
   * @param {ResizeObserverEntry[]} entries
   */
  #repositionOnResize = (entries) => {
    const entry = entries[0];
    if (!entry) return;

    this.#size = { width: entry.contentRect.width, height: entry.contentRect.height };

    const tooltip = this.#activePin ? this.#tooltips.get(this.#activePin) : undefined;
    if (this.#activePin && tooltip) this.#position(this.#activePin, tooltip);
  };

  /**
   * @param {HTMLElement} pin
   */
  #show(pin) {
    const tooltip = this.#tooltips.get(pin);
    if (!tooltip) return;

    if (this.#activePin && this.#activePin !== pin) this.#deactivate(this.#activePin);

    this.#activePin = pin;
    pin.classList.add(ACTIVE_CLASS);
    this.#position(pin, tooltip);
    tooltip.classList.add(VISIBLE_CLASS);
  }

  #hide() {
    if (this.#activePin) this.#deactivate(this.#activePin);
    this.#activePin = null;
  }

  /**
   * @param {HTMLElement} pin
   */
  #deactivate(pin) {
    pin.classList.remove(ACTIVE_CLASS);
    this.#tooltips.get(pin)?.classList.remove(VISIBLE_CLASS);
  }

  /**
   * Places the tooltip above the pin (below when there is no headroom),
   * clamped inside the map box — see the class doc.
   *
   * @param {HTMLElement} pin
   * @param {HTMLElement} tooltip
   */
  #position(pin, tooltip) {
    const width = this.#size.width || this.clientWidth;
    const height = this.#size.height || this.clientHeight;

    const styles = getComputedStyle(tooltip);
    const offset = readLength(styles, '--us-partner-map-tooltip-offset');
    const inset = readLength(styles, '--us-partner-map-tooltip-inset');
    const headroom = readLength(styles, '--us-partner-map-tooltip-headroom');
    const radius = readLength(styles, '--us-partner-map-tooltip-radius');
    const stemWidth = readLength(styles, '--us-partner-map-tooltip-stem-width');

    const pinX = (Number(pin.dataset.x ?? 0) / 100) * width;
    const pinY = (Number(pin.dataset.y ?? 0) / 100) * height;
    const tooltipWidth = tooltip.offsetWidth;
    const tooltipHeight = tooltip.offsetHeight;

    const flipped = pinY - offset - tooltipHeight < headroom;
    const maxLeft = Math.max(inset, width - tooltipWidth - inset);
    const left = clamp(pinX - tooltipWidth / 2, inset, maxLeft);
    const top = flipped ? pinY + offset : pinY - offset - tooltipHeight;
    const stemEdge = radius + stemWidth / 2;
    const stemX = clamp(pinX - left, stemEdge, Math.max(stemEdge, tooltipWidth - stemEdge));

    tooltip.classList.toggle(FLIPPED_CLASS, flipped);
    tooltip.style.setProperty('--us-partner-map-tooltip-x', `${left}px`);
    tooltip.style.setProperty('--us-partner-map-tooltip-y', `${top}px`);
    tooltip.style.setProperty('--us-partner-map-tooltip-stem-x', `${stemX}px`);
  }
}

/**
 * @param {CSSStyleDeclaration} styles
 * @param {string} property - A custom property holding a px length.
 * @returns {number} The length in px (0 when the property is missing).
 */
function readLength(styles, property) {
  const value = Number.parseFloat(styles.getPropertyValue(property));
  return Number.isFinite(value) ? value : 0;
}

/**
 * @param {string | undefined} value - Space-separated state codes (`data-lanes`).
 * @returns {string[]}
 */
function toCodes(value) {
  return (value ?? '').split(' ').filter(Boolean);
}

/**
 * The ranch's one "days in transit" number, the same for every state it
 * serves (business rule 2026-10-06) — there is no per-lane fallback.
 *
 * @param {string | undefined} shippingTime - `data-shipping-time`: the ranch's shipping_time in days ('' when blank).
 * @returns {string | null} "3 days" / "1 day", or null when the ranch has no (or an invalid) shipping time.
 */
function shippingDaysText(shippingTime) {
  const days = Number.parseInt(shippingTime ?? '', 10);
  if (!Number.isInteger(days) || days < 0) return null;
  return `${days} day${days === 1 ? '' : 's'}`;
}

/**
 * @param {string | undefined} value - `data-live-lanes`: one lane list per live ranch, `|`-separated.
 * @param {string} code
 * @returns {number} Live ranches whose lanes contain `code`.
 */
function countShipping(value, code) {
  if (!value) return 0;
  return value.split('|').filter((lanes) => toCodes(lanes).includes(code)).length;
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

if (!customElements.get('us-partner-map-component')) {
  customElements.define('us-partner-map-component', UsPartnerMapComponent);
}
